// src/encounter/structured-note/complaint-lines-model.ts
// PROD-3042 · the pure rules behind complaint-lines.tsx (D-72): how a pick-list change is written, which
// group is "Associated", and the fixed words. Kept apart from the components (react-refresh).
import type { ChiefComplaintHpiCategory } from '@scripsteam/scrips-signal-ds';

/**
 * What one pick-list change means for the complaint document, so it can go out through the page's
 * existing single writes. The page's toggle computes from the document it rendered with, so a change
 * is never expressed as two toggles in one event (the 13 Sep "Clear keeps two" defect):
 *  · nothing changed (a single list re-picking its value) → `none`;
 *  · every value removed → `clear` (ONE write: `onClearCategory`);
 *  · exactly one value added or removed → `toggle` that option;
 *  · a single-choice list picking another value → `toggle` the new one (the page's toggle replaces the
 *    group's value for a SingleChoice group);
 *  · anything else → `unsupported`, and nothing is written. The DS list changes one row per event, so
 *    this cannot come from the control; it is refused rather than half-applied.
 */
export type AttributeChange =
  | { kind: 'none' }
  | { kind: 'clear' }
  | { kind: 'toggle'; optionId: string }
  | { kind: 'unsupported' };

export function planAttributeChange(current: readonly string[], next: readonly string[], single: boolean): AttributeChange {
  const added = next.filter((v) => !current.includes(v));
  const removed = current.filter((v) => !next.includes(v));
  if (added.length === 0 && removed.length === 0) return { kind: 'none' };
  if (next.length === 0) return { kind: 'clear' };
  if (added.length + removed.length === 1) return { kind: 'toggle', optionId: (added[0] ?? removed[0]) as string };
  if (single && added.length === 1) return { kind: 'toggle', optionId: added[0] as string };
  return { kind: 'unsupported' };
}

/**
 * D-72 final (design desk, 6 Oct, after the encounter UX laws): the "Associated signs and symptoms" group is
 * the one the data names "Associated …". HPI offers NO new associated options — new associated findings are
 * captured once, in the ROS. Matched on the group's own title (the data's word for the category), never on an
 * option's words.
 */
export function isAssociatedCategory(category: Pick<ChiefComplaintHpiCategory, 'title'>): boolean {
  return /^associated\b/i.test((category.title ?? '').trim());
}

/** D-72 amendment (design desk, 6 Oct): where each group of the Associated line comes from, and its action. */
export const ASSOCIATED_FROM_HPI = 'recorded in HPI';
export const ASSOCIATED_FROM_ROS = 'from ROS';
export const ADD_IN_ROS = 'Add in ROS';


/** K8 (6 Oct contract): the chief complaint's add line, at rest. */
export const ADD_CHIEF_COMPLAINT = 'Add chief complaint';
export const ADD_ANOTHER_COMPLAINT = 'Add another complaint';
/** A frozen note with no complaint recorded. */
export const CHIEF_COMPLAINT_NOT_RECORDED = 'Not recorded';
